import { TGetExamDetailDto } from "@/api/services/main/learning-progress/dtos/exam/get-exam-detail-by-industry.dto";
import { TExamListItemDto } from "@/api/services/main/learning-progress/dtos/exam/get-exam-list-by-industry.dto";
import { EXAM_VIRTUAL_PROCEDURE_ID } from "@/features/learning-progress/constants/exam-schedule";
import dayjs from "dayjs";

/**
 * Determine the default exam ID based on closest active exam to now
 */
export function getDefaultExamId(exams: TExamListItemDto[]): number | null {
  const activeExams = exams.filter(e => e.status === "active");
  if (activeExams.length === 0) return exams.length > 0 ? exams[0].id : null;
  
  let closestExam = activeExams[0];
  let minDiff = Infinity;
  const now = dayjs();

  for (const exam of activeExams) {
    if (exam.examMonth) {
      const diff = Math.abs(dayjs(exam.examMonth).diff(now, "day"));
      if (diff < minDiff) {
        minDiff = diff;
        closestExam = exam;
      }
    }
  }

  return closestExam.id;
}

/**
 * Parse text and split URLs into structured parts for rendering.
 * Excludes trailing punctuation like `.,;:)]}` that commonly follow URLs in prose.
 *
 * `String.prototype.split` with a capturing group places captured matches at odd
 * indices, so we identify links by index parity instead of re-testing the regex.
 */
export function parseTextWithLinks(text: string) {
  const urlRegex = /(https?:\/\/[^\s<>()"'\]]+[^\s<>()"'\].,;:!?])/;
  const parts = text.split(urlRegex);
  return parts.map((part, idx) =>
    idx % 2 === 1
      ? { type: "link" as const, content: part, key: idx }
      : { type: "text" as const, content: part, key: idx },
  );
}

/**
 * Determine the initial selected procedure ID based on exam method and dates
 */
export function getInitialProcedureId(exam: TGetExamDetailDto | null): number | null {
  if (!exam?.procedures) return null;

  const now = dayjs();
  const datesToCheck: { id: number; date: dayjs.Dayjs }[] = [];

  for (const p of exam.procedures) {
    if (p.endDate) datesToCheck.push({ id: p.id, date: dayjs(p.endDate) });
  }

  if (exam.examMonth) {
    datesToCheck.push({ id: EXAM_VIRTUAL_PROCEDURE_ID.EXAM_IMPLEMENTATION, date: dayjs(exam.examMonth) });
  }

  if (datesToCheck.length === 0) {
    return exam.procedures.length > 0 ? exam.procedures[0].id : null;
  }

  let closestId: number | null = null;
  let minDiff = Infinity;

  for (const item of datesToCheck) {
    if (item.date.isSame(now, "month")) {
      return item.id;
    }

    const diff = Math.abs(item.date.diff(now, "day"));
    if (diff < minDiff) {
      minDiff = diff;
      closestId = item.id;
    }
  }

  return closestId;
}

/**
 * Get description text for a given procedure (real or virtual)
 */
export function getProcedureDescription(
  exam: TGetExamDetailDto | null,
  procedureId: number | null,
): { description: string | null; site: string | null } {
  if (!exam || procedureId === null) return { description: null, site: null };

  const virtualDescriptionMap: Record<number, string | null> = {
    [EXAM_VIRTUAL_PROCEDURE_ID.EXAM_IMPLEMENTATION]: exam.examDayDescription ?? null,
    [EXAM_VIRTUAL_PROCEDURE_ID.RESULT_DETERMINATION]: exam.passFailDescription ?? null,
  };

  if (procedureId in virtualDescriptionMap) {
    return { description: virtualDescriptionMap[procedureId], site: null };
  }

  const proc = exam.procedures?.find((p) => p.id === procedureId) ?? null;
  return { description: proc?.description ?? null, site: proc?.site ?? null };
}

